import React, { FC, useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'next-i18next'
import {
  BidAuctionFragment,
  useCreateAutoBidMutation,
  useCreateBidMutation,
} from '@foros-fe/graphql'
import { Form } from './Form'
import { Button } from '../Button'
import { Switch } from '../Inputs/Switch'
import { TextInput } from '../Inputs/TextInput'
import { SvgIcon } from '../SvgIcon'
import { formatMoney, getErrorTranslations } from '../../utils'
import BidPricePrefix from '@foros-fe/assets/icons/bid-price.svg'

type Props = {
  auction: BidAuctionFragment
}
type Data = {
  price: string
  autoBid: boolean
}

const defaultValues = { price: '', autoBid: false }

export const CreateBid: FC<Props> = ({ auction }) => {
  const { t } = useTranslation('auctions')
  const [createBidMutation] = useCreateBidMutation()
  const [createAutoBidMutation] = useCreateAutoBidMutation()
  const form = useForm<Data>({ defaultValues })

  const { register, watch, getValues, setValue, setError } = form
  const autoBid = watch('autoBid')
  const minimumBidPrice = autoBid ? auction.nextMinimumAutoBidPrice : auction.nextMinimumBidPrice
  const onSubmit = useCallback(
    async ({ price, autoBid }: Data) => {
      const variables = { input: { auction: { id: auction.id }, price } }

      let priceErrors
      if (autoBid) {
        const { data } = await createAutoBidMutation({ variables })

        const priceError = data?.createAutoBid?.error?.price
        if (priceError) {
          priceErrors = getErrorTranslations('CreateAutoBidPriceError', priceError)
        }
      } else {
        const { data } = await createBidMutation({ variables })

        const priceError = data?.createBid?.error?.price
        if (priceError) {
          priceErrors = getErrorTranslations('CreateBidPriceError', priceError)
        }
      }

      if (priceErrors) {
        return setError('price', priceErrors)
      }

      setValue('price', (parseFloat(price) + parseFloat(auction.bidIncrement.amount)).toFixed(1))
    },
    [auction, createAutoBidMutation, createBidMutation, setError, setValue]
  )

  useEffect(() => {
    const price = getValues('price')

    if (
      !price ||
      price === auction.nextMinimumAutoBidPrice.amount ||
      price === auction.nextMinimumBidPrice.amount
    ) {
      setValue('price', minimumBidPrice.amount)
    }
  }, [auction, minimumBidPrice, getValues, setValue])

  const bidStep = formatMoney(auction.bidIncrement)

  return (
    <Form {...form} onSubmit={onSubmit}>
      <TextInput
        label={t('yourBid')}
        type='number'
        step={auction.bidIncrement.amount}
        min={minimumBidPrice.amount}
        hint={t('minBidPrice', {
          nextMinimumBidPrice: formatMoney(minimumBidPrice),
          step: bidStep,
        })}
        prefix={<SvgIcon src={BidPricePrefix} size={20} />}
        className='mb-6'
        {...register('price')}
      />
      <Switch
        title={t('autoBidTitle')}
        hint={t('autoBidHint', { bidStep })}
        className='mb-6'
        {...register('autoBid')}
      />
      <Button
        title={t('bid')}
        value={autoBid ? 'autobid-form' : 'bid-form'}
        className='w-full'
        type='submit'
      />
    </Form>
  )
}
